// TrinkEmlak API Base Response Models

export interface BaseApiResponse<T = unknown> {
  isSuccess: boolean;
  statusCode: number;
  data: T | null;
  message: string;
  errors: string[];
  timeStamp: string;
  apiVersion: string;
}

export interface PaginatedResponse<T> {
  data: T[]; // items yerine data
  pageIndex: number; // pageNumber yerine pageIndex
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export type PaginatedApiResponse<T> = BaseApiResponse<PaginatedResponse<T>>;

// City Types
export interface City {
  id: string;
  name: string;
}

export type CitiesResponse = BaseApiResponse<City[]>;

// District Types
export interface District {
  id: string;
  name: string;
  cityId: string;
}

export type DistrictsResponse = BaseApiResponse<District[]>;

// Auction Types
export interface Auction {
  id: string;
  title: string;
  address: string;
  cityName: string;
  districtName: string;
  description: string;
  startingPrice: number;
  currentHighestBid: number | null;
  totalBidCount: number;
  startDate: string;
  endDate: string;
  status: number;
  isActive: boolean;
  createdDate: string;
  isFeatured: boolean;
  userName: string;
  isFavorite: boolean;
  winnerUserName: string | null;
  image: string;
  realEstateType: number;
  squareMeters: number;
  roomCount: number | null;
  buildingAge: number | null;
  deedStatus: number;
  zoningStatus: number;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
}

// Müzayede detay tipi - API response'una göre
export interface AuctionDetail {
  id: string;
  title: string;
  description: string;
  address: string;
  cityId: string;
  districtId: string | null;
  postalCode: string | null;
  squareMeters: number;
  realEstateType: number;
  buildingAge: number | null;
  roomCount: number | null;
  deedStatus: number;
  zoningStatus: number;
  isFeatured: boolean;
  videoUrl: string | null;
  cityName: string;
  districtName: string;
  startingPrice: number;
  minimumIncrement: number;
  status: number;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  userId: string;
  userName: string;
  winnerUserName: string | null;
  winningBidAmount: number | null;
  isActive: boolean;
  createdDate: string;
  updatedDate: string | null;
  totalBidCount: number;
  currentHighestBid: number | null;
  properties: PropertyDetail[];
  media: MediaDetail;
  isHighestBidder: boolean;
  isFavorite: boolean;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
}

// Mülk özellikleri tipi
export interface PropertyDetail {
  key: string;
  value: string;
}

// Admin için müzayede detay tipi - API response'una göre
export interface AdminAuctionDetail {
  id: string;
  title: string;
  slug: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaKeywords: string | null;
  description: string;
  address: string;
  cityId: string;
  districtId: string;
  postalCode: string;
  squareMeters: number;
  realEstateType: number;
  buildingAge: number;
  roomCount: number;
  housingType: string;
  deedType: string;
  zoningStatus: string;
  isFeatured: boolean;
  videoUrl: string | null;
  cityName: string;
  districtName: string;
  startingPrice: number;
  minimumIncrement: number;
  status: number;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  userId: string;
  userName: string;
  winnerUserName: string | null;
  winningBidAmount: number | null;
  isActive: boolean;
  createdDate: string;
  updatedDate: string | null;
  totalBidCount: number;
  currentHighestBid: number | null;
  properties: PropertyDetail[];
  // Backend'den gelen localizedProperties array'i (opsiyonel, dinamik yapı)
  localizedProperties?: Array<{
    id?: string;
    entityId?: string;
    languageId?: string;
    languageName?: string;
    localeKeyGroup?: number;
    localeKey: string;
    localeValue: string;
    createdDate?: string;
    updatedDate?: string | null;
    isActive?: boolean;
  }>;
  media: MediaDetail;
  isHighestBidder: boolean;
  isFavorite: boolean;
}

export type AdminAuctionDetailResponse = BaseApiResponse<AdminAuctionDetail>;

// Medya detayları tipi
export interface MediaDetail {
  images: string[];
  video: string | null;
}

// Müzayede detay response tipi
export type AuctionDetailResponse = BaseApiResponse<AuctionDetail>;

// Müzayede listesi filtreleme parametreleri
export interface AuctionListFilters {
  status: number[];
  realEstateType?: number[];
  cityId?: string[];
  districtId?: string[];
  minPrice?: number | null;
  maxPrice?: number | null;
}

// Müzayede listesi request parametreleri
export interface AuctionListRequest {
  pageNumber: number;
  pageSize: number;
  status?: number[]; // Optional - hiç seçilmemişse tüm status'lar
  realEstateType?: number[];
  cityId?: string[];
  districtId?: string[];
  minPrice?: number | null;
  maxPrice?: number | null;
  isActive?: boolean | null; // Admin-only parameter, default null
  searchTerm?: string; // Arama terimi
}

export type AuctionsResponse = BaseApiResponse<Auction[]>;
export type AuctionListResponse = PaginatedApiResponse<Auction>;

// Authentication Types
export interface LoginRequest {
  phoneNumber: string;
  password: string;
  rememberMe?: boolean;
}

export interface User {
  id: string;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone: string;
  roles: string[];
  isEDevletVerified: boolean;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
}

// Admin User Types
export interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  accountType: number;
  isEDevletVerified: boolean;
  isActive: boolean;
  role: string;
  cityName: string | null;
  districtName: string | null;
  createdDate: string;
  lastLoginDate: string | null;
}

export interface GetUsersAdminRequest {
  phoneNumber: string | null;
  accountType: number | null;
  isActive: boolean | null;
  isEDevletVerified: boolean | null;
  cityId: string | null;
}

export type GetUsersAdminResponse = PaginatedApiResponse<AdminUser>;

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

// Backend'den gelen response (sadece token bilgileri)
export interface BackendAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

// API route'larımızdan dönen response (sadece user bilgisi, token'lar HttpOnly cookie)
export interface LoginResponse {
  user?: User;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export type AuthApiResponse = BaseApiResponse<LoginResponse>;
export type RefreshTokenApiResponse = BaseApiResponse<RefreshTokenResponse>;

// Register Types
export interface RegisterRequest {
  phoneNumber: string;
  password: string;
  email: string;
  firstName: string;
  lastName: string;
  tcIdentityNumber?: string;
  smsCode: string;
}

// API route'larımızdan dönen response (sadece user bilgisi, token'lar HttpOnly cookie)
export interface RegisterResponse {
  user?: User;
}

export type RegisterApiResponse = BaseApiResponse<RegisterResponse>;

// Change Password Types
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export type ChangePasswordApiResponse = BaseApiResponse<ChangePasswordResponse>;

// User Profile Update Types
export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
}

export type UpdateProfileApiResponse = BaseApiResponse<null>;

// Mülk Türleri Enum
export enum PropertyType {
  LAND = 'land',                  // Arsa
  RESIDENTIAL = 'residential'     // Konut
}

// Tapu Türleri Enum
export enum DeedType {
  KAT = 'kat',                    // Kat Mülkiyetli Tapu
  KAT_IRTIFAK = 'kat_irtifak',    // Kat İrtifakı Tapusu
  YAPI_IRTIFAK = 'yapi_irtifak',  // Yapı İrtifakı Tapusu
  MUSTAKIL = 'mustakil',          // Müstakil Tapu
  ARSA = 'arsa',                  // Arsa Tapusu
  TARLA = 'tarla',                // Tarla Tapusu
  BAG_BAHCE = 'bag_bahce',        // Bağ-Bahçe Tapusu
  CAYIR_MERA = 'cayir_mera',      // Çayır-Mera Tapusu
  ORMAN = 'orman',                // Orman Tapusu
  TICARI = 'ticari',              // Ticari Tapu
  SANAYI = 'sanayi',              // Sanayi Tapusu
  KARMA = 'karma',                // Karma Tapu
  OTHER = 'other'                 // Diğer
}

// Error Types
export interface ApiError {
  message: string;
  errors: string[];
  statusCode: number;
}


// Request Form Types
export interface RequestFormData {
  realEstateType: number; // 1=Residential, 2=Land
  cityId: string;
  districtId: string;
  buildingYear?: string; // Sadece Residential için zorunlu
  address: string;
  deedType: string;
  hasMortgage: boolean;
  mortgageAmount?: string; // İpotek miktarı (hasMortgage true ise zorunlu)
  minimumPrice: string;
  description: string;
  deedImage: File;
}

export interface RequestFormResponse {
  id: string;
  message: string;
  status: string;
}

export type RequestFormApiResponse = BaseApiResponse<RequestFormResponse>;

// User Bid Types - Güncellenmiş yapı
export interface UserBid {
  id: string;
  auctionTitle: string;
  auctionStartDate: string;
  auctionEndDate: string;
  startingPrice: number;
  currentHighestBid: number;
  myBidAmount: number; // Kullanıcının verdiği teklif miktarı
  winningBidAmount: number | null;
  isHighestBidder: boolean;
  cityName: string;
  districtName: string;
  mainImageUrl: string;
  status: number;
  createdDate: string;
  bidId: string | null;
  userId: string;
  depositId: string | null;
  depositCode: string | null;
  depositAmount: number | null;
  depositStatus: number | null;
  depositDate: string | null;
  address: string | null;
  depositDeadline: string | null;
  remainingDays: number | null;
  remainingHours: number | null;
  realEstateType: number | null;
  isFavorite: boolean;
  slug: string;
}

export type UserBidsResponse = BaseApiResponse<UserBid[]>;

// Pending Deposit Auctions - Güncellenmiş yapı
export interface PendingDepositAuction {
  id: string;
  auctionTitle: string;
  auctionStartDate: string;
  auctionEndDate: string;
  startingPrice: number;
  currentHighestBid: number;
  myBidAmount: number; // Kullanıcının verdiği teklif miktarı
  winningBidAmount: number | null;
  isHighestBidder: boolean;
  cityName: string;
  districtName: string;
  mainImageUrl: string;
  status: number;
  createdDate: string;
  bidId: string | null;
  userId: string;
  depositId: string | null;
  depositCode: string | null;
  depositAmount: number | null;
  depositStatus: number | null;
  depositDate: string | null;
  address: string | null;
  depositDeadline: string | null;
  remainingDays: number | null;
  remainingHours: number | null;
  realEstateType: number | null;
  slug: string;
}

export type PendingDepositAuctionsResponse = BaseApiResponse<PendingDepositAuction[]>;

// User Favorites Types
export interface UserFavoritesRequest {
  pageNumber: number;
  pageSize: number;
}

// Favori müzayedeleri için yeni tip - teklif servisi ile aynı yapı
export interface UserFavorite {
  id: string;
  auctionTitle: string;
  auctionStartDate: string;
  auctionEndDate: string;
  startingPrice: number;
  currentHighestBid: number;
  myBidAmount: number | null; // Kullanıcının verdiği teklif miktarı (teklif vermemişse null)
  winningBidAmount: number | null;
  isHighestBidder: boolean;
  cityName: string;
  districtName: string;
  mainImageUrl: string;
  status: number;
  createdDate: string;
  bidId: string | null;
  userId: string;
  depositId: string | null;
  depositCode: string | null;
  depositAmount: number | null;
  depositStatus: number | null;
  depositDate: string | null;
  address: string | null;
  depositDeadline: string | null;
  remainingDays: number | null;
  remainingHours: number | null;
  realEstateType: number | null;
  slug: string;
}

export type UserFavoritesResponse = PaginatedApiResponse<UserFavorite>;

// Favorite Actions Types
export interface AddFavoriteRequest {
  auctionId: string;
}

export interface AddFavoriteResponse {
  message: string;
  isSuccess: boolean;
}

export type AddFavoriteApiResponse = BaseApiResponse<AddFavoriteResponse>;

export interface RemoveFavoriteRequest {
  auctionId: string;
}

export interface RemoveFavoriteResponse {
  message: string;
  isSuccess: boolean;
}

export type RemoveFavoriteApiResponse = BaseApiResponse<RemoveFavoriteResponse>;

// Request Forms Types (User)
export enum RequestFormStatus {
  Pending = 0,    // Beklemede
  Reviewed = 1,   // İncelendi
  Contacted = 2   // İletişim Kuruldu
}

export interface MyRequestForm {
  id: string;
  realEstateType: number;
  status: number;
  cityId: string;
  cityName: string;
  districtId: string;
  districtName: string;
  buildingYear: string | null;
  address: string;
  deedType: string;
  hasMortgage: boolean;
  mortgageAmount: number | null;
  minimumPrice: number;
  description: string;
  deedImageUrl: string;
  userId: string;
  userName: string;
  email: string;
  phoneNumber: string;
  reviewedDate: string | null;
  reviewedByUserId: string | null;
  reviewedByUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
}

export interface MyRequestFormsRequest {
  status?: number;
  pageNumber: number;
  pageSize: number;
}

export type MyRequestFormsResponse = PaginatedApiResponse<MyRequestForm>;

// Admin Request Forms Types
export interface AdminRequestForm {
  id: string;
  realEstateType: number;
  status: number;
  cityId: string;
  cityName: string;
  districtId: string;
  districtName: string;
  buildingYear: string | null;
  address: string;
  deedType: string;
  hasMortgage: boolean;
  mortgageAmount: number | null;
  minimumPrice: number;
  description: string;
  deedImageUrl: string;
  userId: string;
  userName: string;
  email: string;
  phoneNumber: string;
  reviewedDate: string | null;
  reviewedByUserId: string | null;
  reviewedByUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
}

export interface AdminRequestFormsRequest {
  status?: number;
  pageNumber: number;
  pageSize: number;
}

export type AdminRequestFormsResponse = PaginatedApiResponse<AdminRequestForm>;

// SMS Code Types
export enum SmsCodeType {
  ForgotPassword = 1,    // Şifre sıfırlama
  Login = 2,             // Giriş doğrulama
  Register = 3,          // Kayıt doğrulama
  ChangePhone = 4,       // Telefon numarası değiştirme
  TwoFactor = 5,         // İki faktörlü doğrulama
  ChangePassword = 6     // Şifre değiştirme
}

export interface SendSmsCodeRequest {
  phoneNumber: string;
  codeType: SmsCodeType;
}

export interface SmsCodeResponse {
  id: string;
  userId: string;
  code: string;
  codeType: SmsCodeType;
  expirationMinutes: number;
  isSent: boolean;
}

export interface VerifySmsCodeRequest {
  phoneNumber: string;
  code: string;
  codeType: SmsCodeType;
}

export type SendSmsCodeApiResponse = BaseApiResponse<SmsCodeResponse>;
export type VerifySmsCodeApiResponse = BaseApiResponse<boolean>;

// Create Auction Types
export interface LocalizedProperty {
  uniqueSeoCode: string; // "en" veya "tr"
  localeKey: string; // "Title", "Description", "DeedType", "HousingType", "ZoningStatus"
  localeValue: string;
}

export interface CreateAuctionAdminRequest {
  title: string;
  description: string;
  address: string;
  deedType: string;
  housingType: string;
  zoningStatus: string;
  cityId: string;
  districtId: string;
  postalCode: string;
  squareMeters: number;
  realEstateType: number; // RealEstateType enum: 1=Konut, 2=Arsa, 3=Devren Satılık, 4=Devren Kiralık, 5=İşyeri, 6=Turizm Tesisi, 7=Konut Projesi, 8=Tarla
  buildingAge?: number; // Boş bırakılmışsa veya tür için geçerli değilse gönderilmez
  roomCount?: number; // Boş bırakılmışsa veya tür için geçerli değilse gönderilmez
  isFeatured: boolean;
  startingPrice: number;
  minimumIncrement: number;
  startDate: string; // ISO 8601 format
  endDate: string; // ISO 8601 format
  localizedProperties: LocalizedProperty[];
}

export interface CreateAuctionAdminResponse {
  id: string;
}

export type CreateAuctionAdminApiResponse = BaseApiResponse<CreateAuctionAdminResponse>;

export interface AddAuctionImagesRequest {
  auctionId: string;
  imageFiles: File[];
}

export interface AddAuctionImagesResponse {
  success: boolean;
  message: string;
  uploadedImages: string[];
}

export type AddAuctionImagesApiResponse = BaseApiResponse<AddAuctionImagesResponse>;

export interface AddAuctionVideoRequest {
  auctionId: string;
  videoFile: File;
}

export interface AddAuctionVideoResponse {
  success: boolean;
  message: string;
  uploadedVideo: string;
}

export type AddAuctionVideoApiResponse = BaseApiResponse<AddAuctionVideoResponse>;

// Update Auction Admin Types
export interface UpdateAuctionAdminRequest {
  title: string;
  description: string;
  address: string;
  deedType: string;
  housingType: string;
  zoningStatus: string;
  cityId: string;
  districtId: string;
  postalCode: string;
  squareMeters: number;
  realEstateType: number; // RealEstateType enum: 1=Konut, 2=Arsa, 3=Devren Satılık, 4=Devren Kiralık, 5=İşyeri, 6=Turizm Tesisi, 7=Konut Projesi, 8=Tarla
  buildingAge?: number; // Boş bırakılmışsa veya tür için geçerli değilse gönderilmez
  roomCount?: number; // Boş bırakılmışsa veya tür için geçerli değilse gönderilmez
  isFeatured: boolean;
  startingPrice: number;
  minimumIncrement: number;
  startDate: string; // ISO 8601 format
  endDate: string; // ISO 8601 format
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  localizedProperties: LocalizedProperty[];
  // Sıralı resim URL'leri - ilk resim kapak resmi olarak değerlendirilir
  images?: string[];
}

export interface UpdateAuctionAdminResponse {
  id: string;
}

export type UpdateAuctionAdminApiResponse = BaseApiResponse<UpdateAuctionAdminResponse>;

// Reactivate Auction Types
export interface ReactivateAuctionRequest {
  auctionId: string;
  startDate: string; // ISO 8601 format
  endDate: string; // ISO 8601 format
}

export interface ReactivateAuctionResponse {
  id: string;
  message?: string;
}

export type ReactivateAuctionApiResponse = BaseApiResponse<ReactivateAuctionResponse>;

// EIDS Types
export interface RedirectEidsResponse {
  redirectUrl: string;
}

export type RedirectEidsApiResponse = BaseApiResponse<RedirectEidsResponse>;

export interface VerifyEidsRequest {
  yetkiKodu: string;
}

export interface VerifyEidsResponse {
  isEDevletVerified: boolean;
}

export type VerifyEidsApiResponse = BaseApiResponse<VerifyEidsResponse>;
